// @flow

import * as React from "react";

import LinkHandler from "./LinkHandler";
import Icon from "./Icon";
import { type LinkType } from "../../types";
import { ReactComponent as DotsLoader } from "../../../assets/images/dots-loader.svg";
import { ReactComponent as Tick } from "../../../assets/icons/tick.svg";

type PropsType = {
    className: string,
    children: React.Node,
    inline?: boolean,
    styles?: Array<"green" | "green-secondary" | "small" | "simple">,
    to?: string,
    target?: string,
    link?: LinkType,
    counter?: ?number,
    iconBefore?: string,
    iconAfter?: string,
    disabled?: boolean,
    success?: boolean,
    successText?: string,
    loading?: boolean,
    small?: boolean,
    onClick?: () => void,
    type?: string,
};

const Button = (props: PropsType): React.Node => {
    const {
        link,
        inline,
        styles,
        children,
        className,
        counter,
        iconBefore,
        iconAfter,
        success = false,
        successText,
        loading = false,
        disabled,
        small,
        ...remainingProps
    } = props;

    // This is the hook used for enabling/disabling the
    // success state of the button
    const showSuccess = useTimedSuccessState(success);

    const isAnchor = props.to || (link && link.url);
    const RootElement = isAnchor ? LinkHandler : "button";
    const classes = ["button"];

    if (inline) {
        classes.push("button--inline");
    }

    if (small) {
        classes.push("button--small");
    }

    if (styles) {
        for (const style of styles) {
            classes.push(`button--${style}`);
        }
    }

    // Disabled class
    if (disabled) {
        classes.push("is-disabled");
    }

    // Loading class
    if (loading && !disabled) {
        classes.push("is-loading");
    }

    // Success class
    if (showSuccess && !disabled) {
        classes.push("is-success");
    }

    if (className) {
        classes.push(className);
    }

    let autoProps = { ...remainingProps };
    if (link) {
        autoProps = {
            to: link.url,
            target: link.target,
        };
    }

    const renderButtonInner = (): React.Node => {
        return (
            <>
                {iconBefore ? (
                    <Icon
                        className="button__icon button__icon--before"
                        name={iconBefore}
                    />
                ) : null}

                {link ? (
                    link.title
                ) : typeof children === "string" ? (
                    <span
                        className="button__text"
                        dangerouslySetInnerHTML={{ __html: children }}
                    ></span>
                ) : (
                    <span className="button__text">{children}</span>
                )}

                {counter ? (
                    <span className="button__counter">{counter}</span>
                ) : null}

                {iconAfter ? (
                    <Icon
                        className="button__icon button__icon--after"
                        name={iconAfter}
                    />
                ) : null}

                {/* if (showSuccess) {
            return "Success";
        } else if (loading) {
            return <DotsLoader className="button__loader" />; */}
            </>
        );
    };

    return (
        <RootElement
            {...autoProps}
            disabled={loading || disabled}
            className={classes.join(" ")}
        >
            {renderButtonInner()}

            {showSuccess && !disabled ? (
                <div className="button__status">
                    <Tick /> {successText}
                </div>
            ) : loading && !disabled ? (
                <DotsLoader className="button__status" />
            ) : null}
        </RootElement>
    );
};

const useTimedSuccessState = (success: boolean): boolean => {
    // Success state variable
    const [successState, setSuccessState] = React.useState(false);
    // State to hold the timer IDs
    const [timer, setTimer] = React.useState(null);
    // The effect to set the timer
    React.useEffect((): any => {
        if (success) {
            setSuccessState(true);
            setTimer(
                setTimeout(() => {
                    setSuccessState(false);
                }, 1500)
            );
        }
    }, [success]);
    // The effect to clear the timer
    React.useEffect((): any => {
        return () => {
            clearTimeout(timer);
        };
    }, [timer]);
    // Returns the success state
    return successState;
};

Button.defaultProps = {
    className: "",
    styles: ["ghost"],
    children: null,
};

export default Button;
